import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, Calendar, Users, Coffee, Percent, Loader2, Copy, TrendingUp,
  TrendingDown, CheckCircle2, AlertTriangle, Gauge, RefreshCw,
} from "lucide-react";

const VERDICT = {
  competitive: { it: "Competitivo", en: "Competitive", color: "#16a34a", icon: CheckCircle2 },
  push: { it: "Puoi spingere", en: "Room to push", color: "#0ea5e9", icon: TrendingUp },
  below: { it: "Sotto mercato", en: "Below market", color: "#f59e0b", icon: TrendingDown },
  above: { it: "Rischio rifiuto", en: "Refusal risk", color: "#ef4444", icon: AlertTriangle },
};
const WD_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WD_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Defined at MODULE level (not inside the component) so its identity is stable across
// renders; a component created inside render is a new type every time → React remounts
// its children (the <input>), which stole focus after every single keystroke.
const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5 mb-1.5"><Icon className="w-3.5 h-3.5" />{label}</label>
    {children}
  </div>
);
const inputCls = "w-full h-10 rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none";

export default function QuickQuote() {
  const { lang, properties, activeId } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const active = properties.find((p) => p.id === activeId) || properties[0];

  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const plus = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return iso(d); };

  const [checkIn, setCheckIn] = useState(plus(1));
  const [checkOut, setCheckOut] = useState(plus(3));
  const [guests, setGuests] = useState(2);
  const [discount, setDiscount] = useState(10);
  const [breakfast, setBreakfast] = useState(false);
  const [threshold, setThreshold] = useState(5);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [detecting, setDetecting] = useState(null); // date iso being detected, or "all"

  const fmtDate = (s) => { try { const d = new Date(s); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; } catch { return s; } };

  const compute = async () => {
    if (!active?.id) { toast.error(L("Nessuna struttura selezionata", "No property selected")); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { toast.error(L("Il check-out deve essere dopo il check-in", "Check-out must be after check-in")); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${active.id}/quick-quote`, {
        params: { check_in: checkIn, check_out: checkOut, guests, discount_per_night: discount, breakfast, threshold_pct: threshold, lang },
      });
      setRes(data);
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore nel calcolo", "Calculation error")); }
    finally { setLoading(false); }
  };

  const detectPrice = async (dateIso) => {
    if (!active?.id) return;
    setDetecting(dateIso);
    try {
      await api.post(`/properties/${active.id}/detect-price`, { date: dateIso, force: true });
      await compute();
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Rilevazione non riuscita", "Detection failed"));
    } finally { setDetecting(null); }
  };

  const detectAllMissing = async () => {
    if (!active?.id || !res) return;
    const missing = res.nights_detail.filter((n) => n.needs_detection).map((n) => n.date);
    if (!missing.length) return;
    setDetecting("all");
    try {
      for (const dt of missing) {
        await api.post(`/properties/${active.id}/detect-price`, { date: dt, force: true });
      }
      await compute();
      toast.success(L("Prezzi Booking rilevati", "Booking prices detected"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Rilevazione non riuscita", "Detection failed"));
    } finally { setDetecting(null); }
  };

  const copyScript = () => {
    if (!res?.phone_script) return;
    navigator.clipboard?.writeText(res.phone_script);
    toast.success(L("Copiato", "Copied"));
  };

  const verdictBadge = (v) => {
    const cfg = VERDICT[v]; if (!cfg) return <span className="text-xs text-muted-foreground">—</span>;
    const Icon = cfg.icon;
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
        style={{ backgroundColor: `color-mix(in srgb, ${cfg.color} 15%, transparent)`, color: cfg.color }}>
        <Icon className="w-3 h-3" />{L(cfg.it, cfg.en)}
      </span>
    );
  };

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-5xl mx-auto px-6 py-8" data-testid="quick-quote-page">
        <div className="flex items-center gap-3 mb-1">
          <Phone className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">{L("Preventivo al volo", "Quick quote")}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{active?.name} · {active?.city} — {L("dai un prezzo al telefono in pochi secondi, basato sui tuoi prezzi Booking e sui competitor già in memoria.", "give a phone price in seconds, based on your Booking prices and competitors already in memory.")}</p>

        {/* Parameters */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="qq-params">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Field icon={Calendar} label={L("Check-in", "Check-in")}>
              <input type="date" data-testid="qq-checkin" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className={inputCls} />
            </Field>
            <Field icon={Calendar} label={L("Check-out", "Check-out")}>
              <input type="date" data-testid="qq-checkout" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className={inputCls} />
            </Field>
            <Field icon={Users} label={L("Ospiti", "Guests")}>
              <input type="number" min="1" max="30" data-testid="qq-guests" value={guests} onChange={(e) => setGuests(Number(e.target.value))} className={inputCls} />
            </Field>
            <Field icon={Percent} label={L("Sconto €/notte", "Discount €/night")}>
              <input type="number" min="0" step="1" data-testid="qq-discount" value={discount} onChange={(e) => setDiscount(Number(e.target.value))} className={inputCls} />
            </Field>
            <Field icon={Gauge} label={L("Soglia competitor %", "Competitor threshold %")}>
              <input type="number" min="0" step="1" data-testid="qq-threshold" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className={inputCls} />
            </Field>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-muted-foreground">{L("Il prezzo finale per notte = prezzo Booking al netto della tassa di soggiorno e della % sconto struttura (impostata in «La mia struttura»), meno lo «Sconto €/notte» inserito qui sopra.", "The final price per night = Booking price net of city tax and of your property discount % (set in «My property»), minus the «Discount €/night» entered above.")}</p>
            <Button data-testid="qq-calc-btn" onClick={compute} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}{L("Calcola preventivo", "Get quote")}
            </Button>
          </div>
        </motion.div>

        {res && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="qq-result">
            {/* Summary */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo da dare al cliente", "Price to quote")}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-data text-4xl font-bold" data-testid="qq-total">€{res.total_room_net}</span>
                    {res.tax_total > 0 && <span className="text-base text-muted-foreground">+ €{res.tax_total} {L("tassa di soggiorno", "city tax")}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {res.nights} {res.nights === 1 ? L("notte", "night") : L("notti", "nights")} · {L("circa", "about")} <b>€{res.avg_night}/{L("notte", "night")}</b>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{L("Totale con tasse", "Total with tax")}</p>
                  <span className="font-data text-2xl font-semibold" data-testid="qq-grand-total">€{res.grand_total}</span>
                  <div className="mt-1">{verdictBadge(res.overall_verdict)}</div>
                </div>
              </div>
              {/* Phone script */}
              <div className="mt-4 bg-muted/40 rounded-lg p-4 flex items-start gap-3" data-testid="qq-script">
                <Phone className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <p className="text-sm flex-1 italic">"{res.phone_script}"</p>
                <button data-testid="qq-copy-btn" onClick={copyScript} className="p-1.5 text-muted-foreground hover:text-primary shrink-0" title={L("Copia", "Copy")}><Copy className="w-4 h-4" /></button>
              </div>
              {res.needs_detection_nights > 0 && (
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2.5">
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{L(`Per ${res.needs_detection_nights} ${res.needs_detection_nights === 1 ? "data" : "date"} non ho un prezzo Booking rilevato di recente (max 15 giorni). Rilevalo ora per un preventivo preciso.`, `For ${res.needs_detection_nights} date(s) I don't have a recently detected Booking price (max 15 days). Detect it now for an accurate quote.`)}</p>
                  <Button size="sm" variant="outline" data-testid="qq-detect-all-btn" disabled={!!detecting} onClick={detectAllMissing}>
                    {detecting === "all" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}{L("Rileva prezzi mancanti", "Detect missing prices")}
                  </Button>
                </div>
              )}
            </div>

            {/* Per-night breakdown */}
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="qq-nights">
              <div className="px-4 py-3 border-b border-border"><h3 className="text-sm font-semibold">{L("Dettaglio per notte e confronto competitor", "Per-night detail & competitor benchmark")}</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">{L("Data", "Date")}</th>
                      <th className="px-3 py-2 text-right">{L("Booking netto", "Booking net")}</th>
                      <th className="px-3 py-2 text-right">{L("Tuo prezzo/notte", "Your price/night")}</th>
                      <th className="px-3 py-2 text-right">{L("Mediana comp.", "Comp. median")}</th>
                      <th className="px-3 py-2 text-right">{L("Occ. comp.", "Comp. occ.")}</th>
                      <th className="px-3 py-2 text-right">{L("Δ vs mercato", "Δ vs market")}</th>
                      <th className="px-3 py-2">{L("Giudizio", "Verdict")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.nights_detail.map((n, i) => {
                      const wd = (lang === "en" ? WD_EN : WD_IT)[n.weekday];
                      return (
                        <tr key={n.date} className="border-t border-border" data-testid={`qq-night-${i}`}>
                          <td className="px-3 py-2 font-medium">{wd} {fmtDate(n.date)}</td>
                          <td className="px-3 py-2 text-right font-data" title={n.booking_gross != null ? L(`Booking lordo €${n.booking_gross} − tasse${res.self_calibration_pct ? ` − sconto struttura ${res.self_calibration_pct}%` : ""}`, `Booking gross €${n.booking_gross} − tax${res.self_calibration_pct ? ` − property discount ${res.self_calibration_pct}%` : ""}`) : undefined}>
                            {n.needs_detection ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" data-testid={`qq-detect-${i}`} disabled={!!detecting} onClick={() => detectPrice(n.date)}>
                                {detecting === n.date ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />{L("Rileva", "Detect")}</>}
                              </Button>
                            ) : n.unavailable ? (
                              <span className="text-muted-foreground" title={L("Nessuna disponibilità su Booking per questa data", "No Booking availability for this date")}>n.d.</span>
                            ) : (n.own_net != null ? (
                              <span className="inline-flex flex-col items-end">
                                <span>€{n.own_net}</span>
                                {n.min_nights === 2 && (
                                  <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide" title={L("Prezzo da una ricerca con soggiorno minimo 2 notti (la struttura non è prenotabile per 1 sola notte in questa data)", "Price from a min 2-night search (the property isn't bookable for a single night on this date)")}>{L("min. 2 notti", "min. 2 nights")}</span>
                                )}
                              </span>
                            ) : "—")}
                          </td>
                          <td className="px-3 py-2 text-right font-data font-semibold">
                            {n.quote_night != null ? (
                              <span className="inline-flex flex-col items-end">
                                <span>€{n.quote_night}</span>
                                {n.min_nights === 2 && (
                                  <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide" title={L("Prezzo da una ricerca con soggiorno minimo 2 notti", "Price from a min 2-night search")}>{L("min. 2 notti", "min. 2 nights")}</span>
                                )}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-data">{n.competitor?.median_net != null ? `€${n.competitor.median_net}` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{n.competitor?.avg_occupancy != null ? `${n.competitor.avg_occupancy}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data" style={{ color: n.delta_pct == null ? undefined : n.delta_pct > 0 ? "#ef4444" : "#16a34a" }}>{n.delta_pct != null ? `${n.delta_pct > 0 ? "+" : ""}${n.delta_pct}%` : "—"}</td>
                          <td className="px-3 py-2">{verdictBadge(n.verdict)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-[11px] text-muted-foreground space-y-0.5">
                <p>{L("«Booking netto» = prezzo Booking − tassa di soggiorno − % sconto struttura. «Tuo prezzo/notte» = Booking netto − sconto €/notte.", "«Booking net» = Booking price − city tax − property discount %. «Your price/night» = Booking net − discount €/night.")}</p>
                <p>{L("Uso il prezzo Booking della tua struttura solo se rilevato negli ultimi 15 giorni; altrimenti trovi il pulsante «Rileva» per leggerlo al momento su Booking.", "I use your property's Booking price only if detected in the last 15 days; otherwise you'll see a «Detect» button to read it live from Booking.")}</p>
                {res.nights_detail.some((n) => n.min_nights === 2) && (
                  <p className="text-amber-600 dark:text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{L("Le date con l'etichetta «min. 2 notti» non erano prenotabili per una sola notte: il prezzo mostrato viene da una ricerca con soggiorno minimo 2 notti (prezzo totale diviso 2).", "Dates tagged «min. 2 nights» weren't bookable for a single night: the shown price comes from a min 2-night search (total price divided by 2).")}</p>
                )}
                <p>{L("Confronto con i competitor sempre in Room-Only al netto: prezzo Booking del competitor − tassa − % di calibrazione che imposti nella sua scheda; se la tariffa include la colazione e hai inserito il suo valore, la scorporo.", "Competitor comparison is always Room-Only net: competitor Booking price − tax − the calibration % you set on its card; if the rate includes breakfast and you entered its value, I strip it out.")}</p>
                {(() => {
                  const nb = res.nights_detail.reduce((m, n) => Math.max(m, n.competitor?.breakfast_incl_count || 0), 0);
                  const tot = res.nights_detail.reduce((m, n) => Math.max(m, n.competitor?.count || 0), 0);
                  return nb > 0 ? (
                    <p className="text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid="qq-bf-note"><Coffee className="w-3.5 h-3.5" />{L(`Attenzione: ${nb} competitor su ${tot} pubblicano una tariffa con colazione inclusa. Dove non hai inserito il valore colazione nella loro scheda, il confronto può leggermente sovrastimarli.`, `Note: ${nb} of ${tot} competitors publish a rate with breakfast included. Where you haven't entered their breakfast value on their card, the comparison may slightly overstate them.`)}</p>
                  ) : null;
                })()}
                <p><b>{L("Puoi spingere", "Room to push")}</b>: {L("sei sotto i competitor e loro hanno alta occupazione → alza. ", "you're below competitors and they have high occupancy → raise. ")}
                   <b>{L("Rischio rifiuto", "Refusal risk")}</b>: {L("sei sopra il mercato → il cliente potrebbe rifiutare.", "you're above the market → the guest may refuse.")}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
